import { Form, Formik, FormikOnSubmit } from 'formik'
import {
  Button,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalProps,
  VStack,
} from '@ritualco/jutsu'
import { FormikInput, FormikSelect } from 'components/Formik'
import { HeraModal } from 'components/HeraModal'
import { QuestionTooltip } from 'components/QuestionTooltip'
import { useContent } from 'lib/content/react'
import type {
  MerchantListDetails,
  MerchantListDetailsSchema,
  MerchantListTypeOption,
} from './types'
import { useMerchantListEditModal } from './hooks'

type MerchantListEditFormProps = {
  listId: string
  listTypeOptions?: MerchantListTypeOption[]
}

const MerchantListEditForm = ({
  listId,
  listTypeOptions,
}: MerchantListEditFormProps) => {
  const { formatMessage } = useContent()
  return (
    <Form id="merchantListEdit">
      <VStack spacing={6}>
        <FormikInput
          name="name"
          label={
            <QuestionTooltip
              label={formatMessage({
                id: 'pmportal.restaurantLists.listNameTooltip',
              })}
            >
              {formatMessage({ id: 'pmportal.restaurantLists.listName' })}
            </QuestionTooltip>
          }
          placeholder="Optional"
        />
        {listTypeOptions && (
          <FormikSelect
            name="type"
            label={
              <QuestionTooltip
                label={formatMessage({
                  id: 'pmportal.restaurantLists.listTypeTooltip',
                })}
              >
                {formatMessage({ id: 'pmportal.restaurantLists.listType' })}
              </QuestionTooltip>
            }
          >
            {listTypeOptions.map(({ label, value }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </FormikSelect>
        )}
        <FormikInput
          name="listId"
          label={
            <QuestionTooltip
              label={formatMessage({
                id: 'pmportal.restaurantLists.listIdTooltip',
              })}
            >
              {formatMessage({ id: 'pmportal.restaurantLists.listId' })}
            </QuestionTooltip>
          }
          value={listId}
          isDisabled
        />
      </VStack>
    </Form>
  )
}

export type MerchantListEditModalProps = {
  listDetails: MerchantListDetails
  listTypeOptions?: MerchantListTypeOption[]
  onSubmit: FormikOnSubmit<MerchantListDetailsSchema>
} & Omit<ModalProps, 'children'>

export const MerchantListEditModal = (props: MerchantListEditModalProps) => {
  const {
    listDetails,
    listTypeOptions,
    onSubmit,
    onClose,
    ...modalProps
  } = props
  const { formatMessage } = useContent()
  const { listId, initialValues, validationSchema } = useMerchantListEditModal(
    listDetails,
    listTypeOptions
  )

  return (
    <HeraModal size="2xl" onClose={onClose} {...modalProps}>
      <Formik<MerchantListDetailsSchema>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          await onSubmit(values, actions)
          actions.setSubmitting(false)
          onClose()
        }}
      >
        {props => (
          <>
            <ModalHeader>
              {formatMessage({
                id: 'pmportal.restaurantLists.editDetailsTitle',
              })}
            </ModalHeader>
            <ModalBody>
              <MerchantListEditForm
                listId={listId}
                listTypeOptions={listTypeOptions}
              />
            </ModalBody>
            <ModalFooter>
              <Button
                colorScheme="transparent"
                variant="ghost"
                mr={2}
                onClick={onClose}
              >
                {formatMessage({ id: 'pmportal.global.actionCancel' })}
              </Button>
              <Button
                type="submit"
                form="merchantListEdit"
                isLoading={props.isSubmitting}
              >
                {formatMessage({ id: 'pmportal.global.actionSave' })}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </HeraModal>
  )
}
